import { useEffect, useMemo, useRef, useState } from 'react';

import { Star } from '@mui/icons-material';
import {
  Box,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  IconButton,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { format, parseISO } from 'date-fns';
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_RowVirtualizer,
  MRT_SortingState,
  useMaterialReactTable,
} from 'material-react-table';
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';
import { useMutation, useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { ConfirmDialog } from 'src/components/ConfimDialog';
import { useAuthContext } from 'src/context/AuthContextProvider';
import {
  createAlert,
  CreateAlert,
  UpdateAlert,
  updateAlert,
  updateBatchAlertStatus,
} from 'src/services/alerts';
import { setBannerMessage } from 'src/store/display';
import { Alert, AlertType, Status } from 'src/types/alert';
import { Favorite } from 'src/types/favorite';
import { getMarketDisplayLabel } from 'src/utils/markets';

type OperationTableProps = {
  onRowClick: (favorite: Favorite, title: string) => void;
  refetch: () => void;
  isLoading: boolean;
  data: {
    favorite: Favorite;
    alerts: Alert[];
  }[];
};

type NotificationAlertType = {
  name: string;
  type: AlertType;
};

const DISABLED_MARKETS_ALERTS = ['FIAGRO', 'DEBENTURES'];

const notifications: NotificationAlertType[] = [
  {
    name: 'Fatos Relevantes',
    type: AlertType.NEW_RELEVANT_FACTS,
  },
  {
    name: 'Assembleias',
    type: AlertType.NEW_CONVOCATION,
  },
  {
    name: 'Documentos',
    type: AlertType.NEW_DOCUMENT,
  },
  {
    name: 'Artigos',
    type: AlertType.NEW_ARTICLE,
  },
];

export function OperationTable({
  data,
  onRowClick,
  refetch,
  isLoading,
}: OperationTableProps) {
  const rowVirtualizerInstanceRef = useRef<MRT_RowVirtualizer>(null);

  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [batchSwitch, setBatchSwitch] = useState<{
    [key: string]: boolean;
  }>({});
  const [openDialog, setOpenDialog] = useState(false);
  const dispatch = useDispatch();
  const [currentData, setCurrentData] = useState<typeof data>(data);
  const [selectedItem, setSelectedItem] = useState<{
    type: AlertType;
    enabled: boolean;
  } | null>(null);
  const { auth } = useAuthContext();
  const hasAlertPermission =
    auth?.user?.subscription?.plan?.permissions?.alertas;

  const queryClient = useQueryClient();

  const updateAlertBatchStatusMutation = useMutation(
    ({
      alertsToCreate,
      alertsToUpdate,
    }: {
      alertsToCreate?: CreateAlert[];
      alertsToUpdate?: UpdateAlert[];
    }) => updateBatchAlertStatus(alertsToCreate ?? [], alertsToUpdate ?? []),
    {
      onSuccess: () => {
        dispatch(
          setBannerMessage({
            message: 'Atualização de notificações realizada com sucesso',
            type: 'success',
            autoclose: true,
          }),
        );
        queryClient.invalidateQueries({
          queryKey: ['favorites', 'operations'],
        });
        refetch();
      },
      onError: () => {
        dispatch(
          setBannerMessage({
            message:
              'Erro ao atualizar notificações, tente novamente mais tarde',
            type: 'error',
          }),
        );
      },
    },
  );

  const createAlertMutation = useMutation(
    ({ type, favorite }: { type: AlertType; favorite: Favorite }) =>
      createAlert({
        favorite: favorite.id,
        type,
        status: Status.ENABLED,
        rule: {
          operationId: favorite.operation?.hashId?.toString() ?? '',
          documentType: type,
        },
      }),
    {
      onSuccess: () => {
        dispatch(
          setBannerMessage({
            message: 'Notificação criada com sucesso',
            type: 'success',
          }),
        );
        queryClient.invalidateQueries({
          queryKey: ['favorites', 'operations'],
        });
        refetch();
      },
      onError: () => {
        dispatch(
          setBannerMessage({
            message: 'Erro ao criar notificação',
            type: 'error',
          }),
        );
      },
    },
  );

  const updateAlertStatusMutation = useMutation(
    ({ alertId, status }: { alertId: number; status: Status }) =>
      updateAlert(alertId, status),
    {
      onSuccess: () => {
        dispatch(
          setBannerMessage({
            message: 'Notificação atualizada com sucesso',
            type: 'success',
          }),
        );
        queryClient.invalidateQueries({
          queryKey: ['favorites', 'operations'],
        });
        refetch();
      },
      onError: () => {
        dispatch(
          setBannerMessage({
            message: 'Erro ao atualizar notificação',
            type: 'error',
          }),
        );
      },
    },
  );

  const handleChangeBatchStatus = (value?: boolean, alertType?: AlertType) => {
    if (value === undefined || alertType === undefined) {
      return;
    }

    const alertsToCreate = currentData?.filter(
      (item) =>
        item.alerts.find((alert) => alert.type === alertType) === undefined,
    );

    const alertsToUpdate = currentData?.filter(
      (item) =>
        item.alerts.find((alert) => alert.type === alertType) !== undefined,
    );

    updateAlertBatchStatusMutation.mutate({
      alertsToCreate: alertsToCreate?.map((item) => ({
        favorite: item.favorite.id,
        type: alertType,
        rule: {
          operationId: item.favorite.operation?.hashId?.toString() ?? '',
          documentType: alertType,
        },
        status: value ? Status.ENABLED : Status.DISABLED,
      })),
      alertsToUpdate: alertsToUpdate?.map((item) => {
        const alert = item.alerts.find((a) => a.type === alertType);
        return {
          id: alert?.id ?? 0,
          status: value ? Status.ENABLED : Status.DISABLED,
        };
      }),
    });

    setOpenDialog(false);
  };

  const columns = useMemo<
    MRT_ColumnDef<{
      favorite: Favorite;
      alerts: Alert[];
    }>[]
  >(
    () => [
      {
        header: 'Ação',
        size: 80,
        Cell({ row }) {
          return (
            <IconButton
              sx={{
                color: '#1D1D1D',
                fontSize: '12px',
                fontWeight: 400,
                textTransform: 'none',
                letterSpacing: 0,
                padding: 0,
                justifyContent: 'flex-start',
                '&:hover': {
                  background: 'transparent',
                },
                p: 2,
              }}
              onClick={() =>
                onRowClick(
                  row.original.favorite,
                  row.original.favorite.operation?.name ?? '',
                )
              }>
              <Star color="primary" />
            </IconButton>
          );
        },
      },
      {
        accessorKey: 'favorite.operation.name',
        header: 'Operação',
        size: 300,
        Cell({ row }) {
          const operation = row.original.favorite.operation;
          return (
            <Link
              to={`/operacao/${operation?.market?.name.toLowerCase()}/${operation?.slug}/${operation?.hashId}`}
              style={{
                fontSize: '14px',
                fontWeight: 400,
                color: '#1D1D1D',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
              {operation?.name}
            </Link>
          );
        },
      },
      {
        accessorKey: 'favorite.operation.market.name',
        header: 'Mercado',
        size: 150,
        Cell({ row }) {
          const marketName = row.original.favorite.operation?.market?.name;

          return getMarketDisplayLabel(marketName);
        },
      },
      {
        header: 'Última notificação',
        size: 200,
        accessorFn: (row) => {
          const lastAlert = row.alerts
            ?.filter((alert) => alert?.sentAt !== null)
            .sort((a, b) => {
              const dateA = a?.sentAt ? new Date(a.sentAt) : new Date(0);
              const dateB = b?.sentAt ? new Date(b.sentAt) : new Date(0);
              return dateB.getTime() - dateA.getTime();
            })[0]?.sentAt;
          return lastAlert ?? '';
        },
        Cell({ cell }) {
          return cell.getValue()
            ? format(parseISO(cell.getValue() as string), 'dd/MM/yyyy')
            : '';
        },
      },
      {
        header: 'Notificações',
        minSize: 300,
        Header() {
          function handleSwitchChange(value: boolean, alertType: AlertType) {
            setSelectedItem({ type: alertType, enabled: value });
            setOpenDialog(true);
          }

          return (
            <FormControl
              component="fieldset"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
              <FormLabel>Notificações</FormLabel>
              <FormGroup
                aria-label="position"
                row
                sx={(_) => ({
                  display: 'flex',
                  flexWrap: 'nowrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                })}>
                {notifications.map((notification, index) => (
                  <Tooltip
                    key={notification.type}
                    title={
                      !hasAlertPermission
                        ? 'Funcionalidade indisponível para o plano atual'
                        : ''
                    }>
                    <FormControlLabel
                      disabled={!hasAlertPermission}
                      key={index}
                      value={notification.type}
                      control={
                        <Switch
                          disabled={!hasAlertPermission}
                          color="primary"
                          size="small"
                          checked={batchSwitch[notification.type] === true}
                          onChange={(_, checked) =>
                            handleSwitchChange(checked, notification.type)
                          }
                        />
                      }
                      label={notification.name}
                      labelPlacement="bottom"
                      sx={{
                        '& .MuiFormControlLabel-label': {
                          fontSize: '10px',
                        },
                        mx: 0.5,
                      }}
                    />
                  </Tooltip>
                ))}
              </FormGroup>
            </FormControl>
          );
        },
        Cell({ row }) {
          // @luizgustavosaraiva - 2026-03-13 - https://uqbar.atlassian.net/browse/UQ-4258
          // tech debt - fix this when fiagro alerts are implemented

          const disabledAlerts = DISABLED_MARKETS_ALERTS.includes(
            row.original.favorite.operation?.market?.name?.toUpperCase() ?? '',
          );

          if (disabledAlerts) {
            return (
              <Box
                p={2}
                display="flex"
                justifyContent="center"
                alignItems="center"
                width="100%">
                Notificações indisponíveis
              </Box>
            );
          }
          const getAlertStatus = (alertType: AlertType) => {
            const alert = row.original.alerts.find(
              (alert) => alert.type === alertType,
            );
            return alert?.status;
          };

          const handleChange = (value: boolean, alertType: AlertType) => {
            const alert = row.original.alerts.find(
              (alert) => alert.type === alertType,
            );
            if (alert) {
              updateAlertStatusMutation.mutate({
                alertId: alert.id,
                status: value ? Status.ENABLED : Status.DISABLED,
              });

              return;
            }

            createAlertMutation.mutate({
              type: alertType,
              favorite: row.original.favorite,
            });
          };

          return (
            <FormControl component="fieldset">
              <FormGroup
                aria-label="position"
                row
                sx={{
                  display: 'flex',
                  flexWrap: 'nowrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}>
                {notifications.map((notification) => (
                  <Tooltip
                    key={notification.type}
                    title={
                      !hasAlertPermission
                        ? 'Funcionalidade indisponível para o plano atual'
                        : ''
                    }>
                    <FormControlLabel
                      disabled={!hasAlertPermission}
                      key={notification.type}
                      value={notification.type}
                      control={
                        <Switch
                          disabled={!hasAlertPermission}
                          color="primary"
                          size="small"
                          checked={
                            getAlertStatus(notification.type) === Status.ENABLED
                          }
                          onChange={(_, checked) =>
                            handleChange(checked, notification.type)
                          }
                        />
                      }
                      label={notification.name}
                      labelPlacement="bottom"
                      sx={{
                        '& .MuiFormControlLabel-label': {
                          fontSize: '10px',
                          whiteSpace: 'nowrap',
                        },
                        mx: 0.5,
                      }}
                    />
                  </Tooltip>
                ))}
              </FormGroup>
            </FormControl>
          );
        },
      },
    ],
    [currentData, batchSwitch, updateAlertBatchStatusMutation],
  );

  useEffect(() => {
    try {
      rowVirtualizerInstanceRef.current?.scrollToIndex?.(0);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  }, [sorting]);

  useEffect(() => {
    if (data) {
      setCurrentData(data);
    }
  }, [data]);

  useEffect(() => {
    const batchSwitch = notifications.reduce(
      (acc, notification) => {
        const allFavoritesHaveAlerts = data?.every((item) =>
          item.alerts.some((alert) => alert.type === notification.type),
        );

        if (!allFavoritesHaveAlerts || !data?.length) {
          return {
            ...acc,
            [notification.type]: false,
          };
        }

        const allAlertsEnabled = data
          .map((item) =>
            item.alerts.find((alert) => alert.type === notification.type),
          )
          .every((alert) => alert?.status === Status.ENABLED);

        return {
          ...acc,
          [notification.type]: allAlertsEnabled,
        };
      },
      {} as { [key: string]: boolean },
    );
    setBatchSwitch(batchSwitch);
  }, [data]);

  const table = useMaterialReactTable({
    columns,
    data: currentData ?? [], //10,000 rows
    enableBottomToolbar: false,
    enableGlobalFilterModes: true,
    enablePagination: false,
    enableRowNumbers: false,
    enableRowVirtualization: true,
    muiTableContainerProps: {
      sx: {
        maxHeight: '500px',
      },
    },
    onSortingChange: setSorting,
    state: { isLoading, sorting },
    rowVirtualizerInstanceRef,
    rowVirtualizerOptions: { overscan: 5 },
    enableColumnActions: false,
    enableGlobalFilter: true,
    enableColumnDragging: false,
    enableDensityToggle: false,
    enableExpandAll: false,
    enableFullScreenToggle: false,
    enableEditing: false,
    enableHiding: false,
    enableTopToolbar: true,
    localization: MRT_Localization_PT_BR,
    muiTablePaperProps: {
      elevation: 0,
    },
    muiTableHeadRowProps: {
      sx: (theme) => ({
        backgroundColor: theme.palette.grey[200],
      }),
    },
  });

  return (
    <>
      <MaterialReactTable table={table} />
      <ConfirmDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        title={'Mudança em massa'}
        submitButtonColor="inherit"
        submitButtonTextVariant="outlined"
        cancelButtonColor="error"
        cancelButtonTextVariant="contained"
        subTitle={
          <Typography
            fontSize={12}
            fontWeight={400}
            color="#1D1D1D"
            component="span">
            Ao confirmar, você vai ligar ou desligar os alertas para &nbsp;
            <b>todos os favoritos</b>, essa alteração não pode ser desfeita.
            <br />
            Deseja continuar?
          </Typography>
        }
        hasActionButton={true}
        submitAction={() =>
          handleChangeBatchStatus(selectedItem?.enabled, selectedItem?.type)
        }
      />
    </>
  );
}
